import { Stack, Icon, Caption } from '@mediahuis/chameleon-react';
import { Error } from '@mediahuis/chameleon-theme-wl/icons';
import { FormikProps } from 'formik';
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RegistrationFlowFormValues } from '../../types/registrationFlows';
import DatePicker from './DatePicker';
import { useBrandGroupConfig } from '../../hooks/useBrandGroupConfig';
import dayjs from 'dayjs';

import './formikDatePicker.css';
import { TRANSLATION_KEYS } from '../../types/validation';

enum DATE_PICKER_FIELDS {
  DAY = 'birthDateDay',
  MONTH = 'birthDateMonth',
  YEAR = 'birthDateYear',
}

interface FormikDatePickerProps<
  T extends Pick<RegistrationFlowFormValues, 'birthDateDay' | 'birthDateMonth' | 'birthDateYear'>,
> {
  label: string;
  formikProps: FormikProps<T>;
}

// DatePicker component wrapped with Formik helpers
export const FormikDatePicker = <
  T extends Pick<RegistrationFlowFormValues, 'birthDateDay' | 'birthDateMonth' | 'birthDateYear'>,
>({
  label,
  formikProps,
}: FormikDatePickerProps<T>) => {
  const { t } = useTranslation();
  const { errors, touched, setTouched, setFieldValue, values } = formikProps;
  const { brandConfig } = useBrandGroupConfig();

  const [showMinimumAgeWarning, setShowMinimumAgeWarning] = useState<boolean>(false);

  const minimumRegistrationAge = brandConfig?.minimumRegistrationAge ?? null;

  const checkAge = useCallback(() => {
    const day = values.birthDateDay;
    const month = values.birthDateMonth;
    const year = values.birthDateYear;

    if (!day || !month || !year || year.length < 4 || minimumRegistrationAge === null) {
      setShowMinimumAgeWarning(false);
      return;
    }

    const birthDate = dayjs(`${year}-${month}-${day}`);
    const today = dayjs();
    const age = today.diff(birthDate, 'year');

    setShowMinimumAgeWarning(
      birthDate.isValid() && birthDate.isBefore(today) && age < minimumRegistrationAge
    );
  }, [values.birthDateDay, values.birthDateMonth, values.birthDateYear, minimumRegistrationAge]);

  useEffect(() => {
    checkAge();
  }, [checkAge]);

  const onBlur = useCallback(
    (field: DATE_PICKER_FIELDS) => {
      setTouched({ ...touched, [field]: true });
    },
    [setTouched, touched]
  );

  const dayError = useMemo(
    () => !!touched.birthDateDay && !!errors.birthDateDay,
    [errors.birthDateDay, touched.birthDateDay]
  );
  const monthError = useMemo(
    () => !!touched.birthDateMonth && !!errors.birthDateMonth,
    [errors.birthDateMonth, touched.birthDateMonth]
  );
  const yearError = useMemo(
    () => !!touched.birthDateYear && !!errors.birthDateYear,
    [errors.birthDateYear, touched.birthDateYear]
  );
  const hasError = useMemo(
    () => dayError || monthError || yearError,
    [dayError, monthError, yearError]
  );
  const birtDateDayIncomplete = useMemo(
    () => !values.birthDateDay && (!!values.birthDateMonth || !!values.birthDateYear),
    [values.birthDateDay, values.birthDateMonth, values.birthDateYear]
  );
  const birthDateMonthIncomplete = useMemo(
    () => !values.birthDateMonth && (!!values.birthDateDay || !!values.birthDateYear),
    [values.birthDateDay, values.birthDateMonth, values.birthDateYear]
  );
  const birthDateYearIncomplete = useMemo(
    () => !values.birthDateYear && (!!values.birthDateDay || !!values.birthDateMonth),
    [values.birthDateDay, values.birthDateMonth, values.birthDateYear]
  );
  const isIncomplete = useMemo(() => {
    return birtDateDayIncomplete || birthDateMonthIncomplete || birthDateYearIncomplete;
  }, [birtDateDayIncomplete, birthDateMonthIncomplete, birthDateYearIncomplete]);

  return (
    <>
      <label htmlFor={DATE_PICKER_FIELDS.DAY} className="formik-date-picker__label">
        <span className="formik-date-picker__label-text">{label}</span>
      </label>
      <DatePicker
        day={{
          fieldName: DATE_PICKER_FIELDS.DAY,
          value: values.birthDateDay,
          error: dayError,
          label: t('register.birthDate.day'),
          onChange: (value: string) => setFieldValue(DATE_PICKER_FIELDS.DAY, value),
          onBlur: () => onBlur(DATE_PICKER_FIELDS.DAY),
        }}
        month={{
          fieldName: DATE_PICKER_FIELDS.MONTH,
          value: values.birthDateMonth,
          error: monthError,
          label: t('register.birthDate.month'),
          onChange: (value: string) => setFieldValue(DATE_PICKER_FIELDS.MONTH, value),
          onBlur: () => onBlur(DATE_PICKER_FIELDS.MONTH),
        }}
        year={{
          fieldName: DATE_PICKER_FIELDS.YEAR,
          value: values.birthDateYear,
          error: yearError,
          label: t('register.birthDate.year'),
          onChange: (value: string) => setFieldValue(DATE_PICKER_FIELDS.YEAR, value),
          onBlur: () => onBlur(DATE_PICKER_FIELDS.YEAR),
        }}
      />
      {hasError && isIncomplete && (
        <Stack className="error-tooltip mt-2" flexDirection={'row'} data-cy="birthDate-message">
          <Icon as={Error} size="sm" className="mr-1" />
          <Caption size="lg" className="m-0 red-base">
            {t(TRANSLATION_KEYS.REQUIRED_BIRTHDATE)}
          </Caption>
        </Stack>
      )}
      {hasError && !isIncomplete && (
        <Stack className="error-tooltip mt-2" flexDirection={'row'} data-cy="birthDate-message">
          <Icon as={Error} size="sm" className="mr-1" />
          <Caption size="lg" className="m-0 red-base">
            {t(TRANSLATION_KEYS.INVALID_BIRTH_DATE)}
          </Caption>
        </Stack>
      )}
      {showMinimumAgeWarning && !hasError && (
        <Stack className="error-tooltip mt-2" flexDirection={'row'} data-cy="minimum-age-warning">
          <Caption
            size="lg"
            className="m-0 red-base"
            style={{ display: 'inline-flex', alignItems: 'center' }}>
            <Icon as={Error} size="sm" className="mr-2" />
            {t('register.error.minimumAge', { minimumAge: minimumRegistrationAge })}
          </Caption>
        </Stack>
      )}
    </>
  );
};
